import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { hasGivenConsent, setConsentGiven, getCookiePreferences, setCookiePreferences, CookiePreferences, clearOptionalCookies } from '@/lib/cookies';
import { CookieProvider } from '@/contexts/CookieContext';
import { useTranslation } from '@/hooks/useTranslation';
interface CookieConsentProps {
  children: React.ReactNode;
}
const CookieConsent: React.FC<CookieConsentProps> = ({
  children
}) => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences());
  const openSettings = () => {
    setShowSettings(true);
  };
  useEffect(() => {
    // Show banner if user hasn't given consent yet
    setShowBanner(!hasGivenConsent());
  }, []);
  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true
    };
    setCookiePreferences(allAccepted);
    setConsentGiven();
    setShowBanner(false);
  };
  const acceptNecessaryOnly = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false
    };
    setCookiePreferences(necessaryOnly);
    setConsentGiven();
    clearOptionalCookies();
    setShowBanner(false);
  };
  const savePreferences = () => {
    setCookiePreferences(preferences);
    setConsentGiven();
    setShowBanner(false);
    setShowSettings(false);

    // Clear cookies if user disabled them
    if (!preferences.analytics) {
      clearOptionalCookies();
    }
  };
  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };
  return <CookieProvider onOpenSettings={openSettings}>
      {children}
      {showBanner && <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:max-w-md z-50 p-4 shadow-lg bg-background border">
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2 text-base">{t.cookies.banner.title}</h3>
            <p className="text-muted-foreground text-sm">
              {t.cookies.banner.description}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="default" size="sm" onClick={acceptAll} className="flex-1 h-14 sm:h-9 text-base sm:text-sm">
              {t.cookies.banner.acceptAll}
            </Button>
            <Button variant="outline" size="sm" onClick={acceptNecessaryOnly} className="flex-1 h-14 sm:h-9 text-base sm:text-sm">
              {t.cookies.banner.necessaryOnly}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} className="flex-1 h-14 sm:h-9 text-base sm:text-sm">
              {t.cookies.banner.settings}
            </Button>
          </div>
        </div>
      </Card>}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="w-[90vw] max-w-sm max-h-[75vh] sm:max-h-[80vh] overflow-y-auto mx-auto rounded-lg">
          <DialogHeader>
            <DialogTitle>{t.cookies.dialog.title}</DialogTitle>
            <DialogDescription>
              {t.cookies.dialog.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="necessary">{t.cookies.dialog.necessary.label}</Label>
                <p className="text-xs text-muted-foreground">
                  {t.cookies.dialog.necessary.description}
                </p>
              </div>
              <Switch id="necessary" checked={preferences.necessary} disabled={true} />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="analytics">{t.cookies.dialog.analytics.label}</Label>
                <p className="text-xs text-muted-foreground">
                  {t.cookies.dialog.analytics.description}
                </p>
              </div>
              <Switch id="analytics" checked={preferences.analytics} onCheckedChange={checked => updatePreference('analytics', checked)} />
            </div>
          </div>
          
          <DialogFooter className="flex flex-col-reverse gap-3 sm:flex-row sm:gap-2">
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              {t.cookies.dialog.cancel}
            </Button>
            <Button onClick={savePreferences}>
              {t.cookies.dialog.savePreferences}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CookieProvider>;
};
export default CookieConsent;